import React from 'react';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, RHFTextField } from '@emfood/shared';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Stack,
  Dialog,
  Button,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import { BuyerOrderProductModel } from 'src/api/models/orders';
import { useAddWeightToProduct } from 'src/api/hooks/api-hooks';

// Props for the ActualWeightDialog component
interface ActualWeightDialogProps {
  open: boolean;
  onClose: () => void;
  product: BuyerOrderProductModel;
  onSubmit: () => void;
  orderId: string;
  title?: string;
}

const ActualWeightSchema = Yup.object().shape({
  actualWeight: Yup.string()
    .required('Actual Weight is required')
    .label('Actual Weight')
    .test('is-number', 'Actual Weight must be a number', (value) => {
      if (value) {
        return !Number.isNaN(Number(value));
      }
      return true;
    }),
});

const ActualWeightDialog: React.FC<ActualWeightDialogProps> = ({
  open,
  onClose,
  onSubmit,
  product,
  orderId,
  title = 'Please provide the exact weight that was delivered to you',
}) => {
  const defaultValues = {
    actualWeight: '',
  };

  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm({
    resolver: yupResolver(ActualWeightSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
    reset,
  } = methods;

  const { mutate } = useAddWeightToProduct();

  const handleFormSubmit = handleSubmit((data) => {
    if (data.actualWeight) {
      mutate(
        {
          body: { weight: Number(data.actualWeight) },
          pathParams: { orderId, orderProductId: product.id },
        },
        {
          onSuccess: () => {
            onSubmit();
            onClose();
          },
        }
      );
    } else {
      enqueueSnackbar('Actual weight is required', { variant: 'error' });
    }
  });

  return createPortal(
    <FormProvider methods={methods} onSubmit={handleFormSubmit}>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Stack sx={{ mt: 2 }} spacing={2}>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {product.productName}
              </Typography>
              {product.estimatedWeight && (
                <Typography>Guide weight: {product.estimatedWeight}kg</Typography>
              )}
            </Box>
            <RHFTextField
              name="actualWeight"
              label="Actual Weight (kg)"
              fullWidth
              placeholder="Enter the actual weight in kilograms"
              inputProps={{ inputMode: 'numeric' }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <LoadingButton
            loading={isSubmitting}
            onClick={handleFormSubmit}
            variant="contained"
            disabled={isSubmitting || !isDirty}
          >
            Save Changes
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </FormProvider>,
    document.body
  );
};

export default ActualWeightDialog;
